import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import {
  type FeatureFlagName,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

type FeatureFlaggedRouteProps = Readonly<{
  children: ReactElement;
  flag: FeatureFlagName;
  redirectTo?: (typeof APP_ROUTE)[keyof typeof APP_ROUTE];
}>;

/**
 * Renders a route only while its feature flag is on, redirecting elsewhere when it is off. Gating
 * here rather than inside the page keeps the page's hooks — and its queries — from running at all.
 *
 * The loader is shown only while the flag set is genuinely still resolving. Once it has settled —
 * including when the fetch was rejected — an absent flag counts as off and redirects, so a failing
 * flag service can't hold the route on a spinner.
 */
export const FeatureFlaggedRoute = ({
  children,
  flag,
  redirectTo = APP_ROUTE.HOME,
}: FeatureFlaggedRouteProps) => {
  const settled = useAppSelector((state) => state.featureFlags.settled);
  const isEnabled = useAppSelector(selectIsFeatureFlagEnabled(flag));

  // Hold the route until the flags land so an enabled feature never flashes the redirect.
  if (!settled) {
    return <FullScreenLoading />;
  }

  if (!isEnabled) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};
